import type { Rng } from "../rng";
import { type ChoiceRound, createChoiceDuel, shuffledRound } from "./framework/choiceDuel";

// --- Times Table Duel -----------------------------------------------------

/** Wrong answers a child could plausibly reach, never a random number. */
function timesDistractors(a: number, b: number, rng: Rng): number[] {
	const near = [a * b + a, a * b - a, a * b + b, a * b - b, a * (b + 1), (a - 1) * b];
	const usable = [...new Set(near)].filter((value) => value > 0 && value !== a * b);
	return rng.shuffle(usable).slice(0, 3);
}

export const timesTables = createChoiceDuel({
	id: "times",
	category: "education",
	title: "Times Table Duel",
	emoji: "✖️",
	blurb: "Race your friend through the times tables.",
	howTo: [
		"A times-table question appears for both of you at once.",
		"Tap the answer before the timer runs out.",
		"Right answers score, and the fastest right answer scores an extra point.",
	],
	rounds: 10,
	questionMs: 10_000,
	revealMs: 2200,
	generate(rng, count) {
		const rounds: ChoiceRound[] = [];
		for (let i = 0; i < count; i++) {
			const a = 2 + rng.int(11);
			const b = 2 + rng.int(11);
			const answer = a * b;
			const wrong = timesDistractors(a, b, rng);
			// A tiny table can run out of near-misses; pad from just above.
			//
			// The step has to come from its own counter, not from `wrong.length`.
			// Deriving it from the length meant that when a candidate was already
			// present nothing was pushed, the length did not move, and the next
			// candidate was the same one — a loop with no way out. It happened
			// whenever the square numbers collapsed the near-miss set to two, so
			// 3 × 3 hung the room outright.
			for (let step = 1; wrong.length < 3; step++) {
				const filler = answer + step;
				if (!wrong.includes(filler)) wrong.push(filler);
			}
			rounds.push(
				shuffledRound(rng, {
					id: `t${i}`,
					prompt: `${a} × ${b}`,
					glyph: `${a} × ${b}`,
					choices: [String(answer), ...wrong.map(String)],
					correct: String(answer),
				}),
			);
		}
		return rounds;
	},
});

// --- Spelling Bee Duel ----------------------------------------------------

interface SpellingEntry {
	word: string;
	wrong: [string, string, string];
}

const SPELLINGS: readonly SpellingEntry[] = [
	{ word: "necessary", wrong: ["neccessary", "necesary", "neccesary"] },
	{ word: "beautiful", wrong: ["beutiful", "beautifull", "beatiful"] },
	{ word: "separate", wrong: ["seperate", "seperete", "separete"] },
	{ word: "definitely", wrong: ["definately", "definetly", "definitly"] },
	{ word: "friend", wrong: ["freind", "frend", "friennd"] },
	{ word: "believe", wrong: ["beleive", "belive", "beleave"] },
	{ word: "because", wrong: ["becuase", "becouse", "becasue"] },
	{ word: "different", wrong: ["diffrent", "diferent", "differant"] },
	{ word: "favourite", wrong: ["favorite", "favourit", "faverite"] },
	{ word: "tomorrow", wrong: ["tommorow", "tomorow", "tommorrow"] },
	{ word: "surprise", wrong: ["suprise", "surprize", "surpirse"] },
	{ word: "library", wrong: ["libary", "libraray", "librery"] },
	{ word: "February", wrong: ["Febuary", "Februray", "Febraury"] },
	{ word: "island", wrong: ["iland", "islend", "isaland"] },
	{ word: "chocolate", wrong: ["choclate", "chocolat", "chocalate"] },
	{ word: "important", wrong: ["importent", "inportant", "importatnt"] },
	{ word: "knowledge", wrong: ["knowlege", "knoledge", "knowladge"] },
	{ word: "sentence", wrong: ["sentance", "sentense", "senetence"] },
	{ word: "science", wrong: ["sience", "sciance", "scince"] },
	{ word: "eighth", wrong: ["eigth", "eightth", "eith"] },
	{ word: "rhythm", wrong: ["rythm", "rhythem", "rhytm"] },
	{ word: "queue", wrong: ["que", "queu", "quee"] },
	{ word: "weird", wrong: ["wierd", "weerd", "wired"] },
	{ word: "castle", wrong: ["casle", "cassle", "castel"] },
	{ word: "brought", wrong: ["brout", "brougth", "braught"] },
	{ word: "thought", wrong: ["thougt", "thort", "thaught"] },
	{ word: "beginning", wrong: ["begining", "beginnning", "beggining"] },
	{ word: "grammar", wrong: ["grammer", "gramar", "grammor"] },
	{ word: "possible", wrong: ["posible", "possable", "possibel"] },
	{ word: "answer", wrong: ["anser", "ansewr", "answar"] },
];

export const spellingBee = createChoiceDuel({
	id: "spelling",
	category: "education",
	title: "Spelling Bee Duel",
	emoji: "🐝",
	blurb: "Four spellings, one of them right. Spot it first.",
	howTo: [
		"Four spellings of the same word appear.",
		"Tap the one that's spelled correctly.",
		"The fastest correct tap earns an extra point.",
	],
	rounds: 8,
	questionMs: 12_000,
	generate(rng, count) {
		return rng.sample(SPELLINGS, count).map((entry, i) =>
			shuffledRound(rng, {
				id: `s${i}`,
				prompt: "Which spelling is right?",
				choices: [entry.word, ...entry.wrong],
				correct: entry.word,
			}),
		);
	},
});

// --- Science Sorter -------------------------------------------------------

interface SortItem {
	glyph: string;
	name: string;
	group: string;
}

const SORT_GROUPS: readonly { question: string; groups: string[]; items: SortItem[] }[] = [
	{
		question: "Solid, liquid or gas?",
		groups: ["Solid", "Liquid", "Gas"],
		items: [
			{ glyph: "🧊", name: "ice", group: "Solid" },
			{ glyph: "💧", name: "water", group: "Liquid" },
			{ glyph: "🎈", name: "the air in a balloon", group: "Gas" },
			{ glyph: "🪨", name: "a rock", group: "Solid" },
			{ glyph: "🥛", name: "milk", group: "Liquid" },
			{ glyph: "☁️", name: "steam", group: "Gas" },
		],
	},
	{
		question: "Animal, plant or neither?",
		groups: ["Animal", "Plant", "Neither"],
		items: [
			{ glyph: "🐙", name: "an octopus", group: "Animal" },
			{ glyph: "🌻", name: "a sunflower", group: "Plant" },
			{ glyph: "🪨", name: "a pebble", group: "Neither" },
			{ glyph: "🦋", name: "a butterfly", group: "Animal" },
			{ glyph: "🌵", name: "a cactus", group: "Plant" },
			{ glyph: "⛈️", name: "a thundercloud", group: "Neither" },
		],
	},
	{
		question: "Where does it live?",
		groups: ["Ocean", "Desert", "Forest"],
		items: [
			{ glyph: "🐬", name: "a dolphin", group: "Ocean" },
			{ glyph: "🐪", name: "a camel", group: "Desert" },
			{ glyph: "🦌", name: "a deer", group: "Forest" },
			{ glyph: "🐠", name: "a clownfish", group: "Ocean" },
			{ glyph: "🦂", name: "a scorpion", group: "Desert" },
			{ glyph: "🦉", name: "an owl", group: "Forest" },
		],
	},
	{
		question: "Which one is it?",
		groups: ["Insect", "Bird", "Fish"],
		items: [
			{ glyph: "🐝", name: "a bee", group: "Insect" },
			{ glyph: "🦅", name: "an eagle", group: "Bird" },
			{ glyph: "🐟", name: "a fish", group: "Fish" },
			{ glyph: "🐞", name: "a ladybird", group: "Insect" },
			{ glyph: "🦜", name: "a parrot", group: "Bird" },
			{ glyph: "🦈", name: "a shark", group: "Fish" },
		],
	},
];

export const scienceSorter = createChoiceDuel({
	id: "sciencesort",
	category: "education",
	title: "Science Sorter",
	emoji: "🔬",
	blurb: "Sort things into the right group before your friend does.",
	howTo: [
		"A picture appears with a question above it.",
		"Tap the group it belongs to.",
		"Quickest correct answer gets a bonus point.",
	],
	rounds: 9,
	questionMs: 9000,
	revealMs: 2200,
	generate(rng, count) {
		const rounds: ChoiceRound[] = [];
		for (let i = 0; i < count; i++) {
			const set = SORT_GROUPS[rng.int(SORT_GROUPS.length)];
			const item = set.items[rng.int(set.items.length)];
			rounds.push(
				shuffledRound(rng, {
					id: `x${i}`,
					prompt: set.question,
					glyph: item.glyph,
					note: `${item.name} — ${item.group}`,
					choices: [...set.groups],
					correct: item.group,
				}),
			);
		}
		return rounds;
	},
});

// --- Where in the World ---------------------------------------------------

const CAPITALS: readonly [string, string][] = [
	["France", "Paris"],
	["Japan", "Tokyo"],
	["Kenya", "Nairobi"],
	["Brazil", "Brasília"],
	["Canada", "Ottawa"],
	["Egypt", "Cairo"],
	["India", "New Delhi"],
	["Italy", "Rome"],
	["Mexico", "Mexico City"],
	["Norway", "Oslo"],
	["Peru", "Lima"],
	["Spain", "Madrid"],
	["Thailand", "Bangkok"],
	["Australia", "Canberra"],
	["Greece", "Athens"],
	["Portugal", "Lisbon"],
];

const LANDMARKS: readonly { glyph: string; prompt: string; correct: string; others: string[] }[] = [
	{
		glyph: "🗼",
		prompt: "Where is the Eiffel Tower?",
		correct: "France",
		others: ["Italy", "Spain", "Belgium"],
	},
	{
		glyph: "🏜️",
		prompt: "Which is the biggest hot desert?",
		correct: "The Sahara",
		others: ["The Gobi", "The Mojave", "The Atacama"],
	},
	{
		glyph: "🏔️",
		prompt: "Which is the tallest mountain?",
		correct: "Everest",
		others: ["K2", "Kilimanjaro", "Mont Blanc"],
	},
	{
		glyph: "🌊",
		prompt: "Which is the longest river?",
		correct: "The Nile",
		others: ["The Amazon", "The Thames", "The Danube"],
	},
	{
		glyph: "🐨",
		prompt: "Where do koalas live in the wild?",
		correct: "Australia",
		others: ["Brazil", "Canada", "India"],
	},
	{
		glyph: "🧊",
		prompt: "Which continent has no countries?",
		correct: "Antarctica",
		others: ["Africa", "Europe", "Asia"],
	},
];

export const geographyDuel = createChoiceDuel({
	id: "geography",
	category: "education",
	title: "Where in the World",
	emoji: "🌍",
	blurb: "Capitals, rivers and mountains — answer faster than your friend.",
	howTo: [
		"A question about somewhere in the world appears.",
		"Tap the answer you think is right.",
		"Fastest correct answer earns a bonus point.",
	],
	rounds: 8,
	questionMs: 12_000,
	generate(rng, count) {
		const rounds: ChoiceRound[] = [];
		// Shuffled fresh and walked straight through, so no question can come
		// up twice in the same game.
		const capitals = rng.shuffle(CAPITALS);
		const landmarks = rng.shuffle(LANDMARKS);
		let capitalAt = 0;
		let landmarkAt = 0;

		for (let i = 0; i < count; i++) {
			// Alternate capitals and landmarks so it never feels like a list.
			const wantCapital = i % 2 === 0 || landmarkAt >= landmarks.length;
			if (wantCapital && capitalAt < capitals.length) {
				const [country, capital] = capitals[capitalAt++];
				// Distractors drawn per round, not the same three every time.
				const others = rng
					.sample(
						capitals.filter(([name]) => name !== country),
						3,
					)
					.map(([, other]) => other);
				rounds.push(
					shuffledRound(rng, {
						id: `g${i}`,
						prompt: `What is the capital of ${country}?`,
						glyph: "🏙️",
						choices: [capital, ...others],
						correct: capital,
					}),
				);
			} else {
				const item = landmarks[landmarkAt++];
				rounds.push(
					shuffledRound(rng, {
						id: `g${i}`,
						prompt: item.prompt,
						glyph: item.glyph,
						choices: [item.correct, ...item.others],
						correct: item.correct,
					}),
				);
			}
		}
		return rounds;
	},
});

// --- Note Match Duel ------------------------------------------------------

/** Semitone steps above A3, so every tone is in tune with the others. */
const SCALE = [220, 247, 262, 294, 330, 349, 392, 440, 494, 523, 587, 659];

export const noteMatch = createChoiceDuel({
	id: "notematch",
	category: "music",
	title: "Note Match Duel",
	emoji: "🎵",
	blurb: "Listen to two notes and say which one was higher.",
	howTo: [
		"Tap the speaker to hear the two notes.",
		"Decide whether the second note was higher, lower or the same.",
		"Fastest correct ear wins the point.",
	],
	rounds: 8,
	questionMs: 14_000,
	generate(rng, count) {
		const rounds: ChoiceRound[] = [];
		for (let i = 0; i < count; i++) {
			const first = rng.int(SCALE.length);
			// Sometimes the same note twice — the "same" answer has to be real.
			const same = rng.next() < 0.2;
			const second = same ? first : rng.int(SCALE.length);
			const correct =
				SCALE[second] > SCALE[first]
					? "Higher"
					: SCALE[second] < SCALE[first]
						? "Lower"
						: "The same";
			rounds.push({
				id: `n${i}`,
				prompt: "Was the second note higher or lower?",
				glyph: "🎧",
				choices: ["Higher", "Lower", "The same"],
				answer: ["Higher", "Lower", "The same"].indexOf(correct),
				tones: `${SCALE[first]},${SCALE[second]}`,
			});
		}
		return rounds;
	},
});

// --- Would You Rather -----------------------------------------------------

const WOULD_YOU_RATHER: readonly [string, string][] = [
	["Fly like a bird", "Breathe underwater"],
	["Have a pet dragon", "Have a pet dinosaur"],
	["Always be too hot", "Always be too cold"],
	["Eat only pizza forever", "Eat only ice cream forever"],
	["Be invisible", "Be super strong"],
	["Live in a treehouse", "Live in a castle"],
	["Talk to animals", "Speak every language"],
	["Never do homework again", "Never tidy your room again"],
	["Have hands for feet", "Have feet for hands"],
	["Be famous for singing", "Be famous for football"],
	["Travel to space", "Travel to the bottom of the sea"],
	["Have a robot butler", "Have a magic carpet"],
	["Only ever whisper", "Only ever shout"],
	["Be able to freeze time", "Be able to rewind time"],
	["Have a slide instead of stairs", "Have a trampoline floor"],
	["Sneeze glitter", "Burp bubbles"],
];

export const wouldYouRather = createChoiceDuel({
	id: "wouldyourather",
	category: "party",
	title: "Would You Rather",
	emoji: "🤔",
	blurb: "Two silly choices. Score a point every time you both pick the same one.",
	howTo: [
		"You both see the same two silly options.",
		"Pick the one you'd rather.",
		"If you both picked the same, you both score!",
	],
	rounds: 8,
	questionMs: 15_000,
	revealMs: 3500,
	scoring: "match",
	generate(rng, count) {
		return rng.sample(WOULD_YOU_RATHER, count).map(([left, right], i) => ({
			id: `w${i}`,
			prompt: "Would you rather…",
			glyph: "🤔",
			choices: [left, right],
			// No right answer: agreeing is what scores.
			answer: -1,
		}));
	},
});

// --- Math Sprint ----------------------------------------------------------

/** Mixed operations, unlike Times Table Duel — and every answer is a whole number. */
function sprintRound(rng: Rng, index: number): ChoiceRound {
	const kind = rng.int(3);
	let prompt: string;
	let answer: number;

	if (kind === 0) {
		const a = 10 + rng.int(80);
		const b = 10 + rng.int(80);
		prompt = `${a} + ${b}`;
		answer = a + b;
	} else if (kind === 1) {
		const a = 30 + rng.int(70);
		const b = 5 + rng.int(a - 5);
		prompt = `${a} − ${b}`;
		answer = a - b;
	} else {
		const a = 3 + rng.int(9);
		const b = 3 + rng.int(9);
		prompt = `${a} × ${b}`;
		answer = a * b;
	}

	const wrong = new Set<number>();
	// Near misses only: a wildly wrong option is no test at all.
	for (const delta of rng.shuffle([1, -1, 2, -2, 10, -10, 9, -9])) {
		if (wrong.size >= 3) break;
		const value = answer + delta;
		if (value > 0 && value !== answer) wrong.add(value);
	}

	return shuffledRound(rng, {
		id: `sp${index}`,
		prompt,
		glyph: prompt,
		choices: [String(answer), ...[...wrong].map(String)],
		correct: String(answer),
	});
}

export const mathSprint = createChoiceDuel({
	id: "mathsprint",
	category: "racing",
	title: "Math Sprint",
	emoji: "🏃",
	blurb: "Every right answer moves your runner. First past the post wins.",
	howTo: [
		"A sum appears for both of you at once.",
		"Every correct answer moves your runner along the track.",
		"The fastest correct answer moves you two steps instead of one.",
		"Furthest along after twelve sums wins the race.",
	],
	rounds: 12,
	questionMs: 9000,
	revealMs: 1800,
	generate(rng, count) {
		return Array.from({ length: count }, (_, index) => sprintRound(rng, index));
	},
});
